import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import webSocketEventBus from '../services/webSocketEventBus';
import {
  MessageSquare, ChevronRight, Search
} from 'lucide-react';
import Pagination from '../components/Pagination';

/**
 * MessagesPage — Secure messaging threads between patients and doctors.
 */
const MessagesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDoctor = user?.role === 'DOCTOR';
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [perSymptomUnread, setPerSymptomUnread] = useState({});
  const ITEMS_PER_PAGE = 10;

  // Listen for per-symptom unread counts from NotificationSystem
  useEffect(() => {
    const handler = (counts) => {
      if (counts?.perSymptom) setPerSymptomUnread(counts.perSymptom);
    };
    webSocketEventBus.on('unreadCounts', handler);

    // Compute initial counts from localStorage immediately
    try {
      const saved = localStorage.getItem('symptoconnect_notifications');
      const parsed = saved ? JSON.parse(saved) : [];
      const counts = {};
      for (const n of parsed) {
        if (!n.read && n.data?.symptomId) {
          counts[n.data.symptomId] = (counts[n.data.symptomId] || 0) + 1;
        }
      }
      setPerSymptomUnread(counts);
    } catch {}

    // Also request from NotificationSystem (may have fresher data after API load)
    webSocketEventBus.emit('requestNotificationCounts');
    const timer = setTimeout(() => webSocketEventBus.emit('requestNotificationCounts'), 1500);

    return () => {
      webSocketEventBus.off('unreadCounts', handler);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        // Threads are typically associated with symptoms
        const endpoint = isDoctor ? '/symptoms?status=OPEN&page=1&limit=50' : '/symptoms/me?page=1&limit=50';
        const res = await api.get(endpoint).catch(() => null);
        const symptoms = res?.data?.data || res?.data || [];
        const symptomsArr = Array.isArray(symptoms) ? symptoms : [];
        // Build threads from symptoms, sorted by latest activity first
        const built = symptomsArr.filter(s => s.status !== 'CLOSED').map(s => ({
          id: s._id,
          title: s.title || 'Untitled',
          patientName: isDoctor
            ? (s.userId?.name || s.patient?.name || 'Patient')
            : (s.consultingDoctors?.[0]?.doctorName || s.responses?.[0]?.doctorId?.name || 'Care Team'),
          lastActivity: s.updatedAt || s.createdAt,
          status: s.status,
          severity: s.severity,
        }));
        built.sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
        setThreads(built);
      } catch (err) {
        console.error('Failed to fetch messages:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filtered = threads.filter(t =>
    !searchTerm ||
    t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.patientName?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paged = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  return (
    <div className="space-y-6 py-6">
      <div>
        <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">Messages</h2>
        <p className="text-slate-500 text-sm mt-1">Secure conversations with your {isDoctor ? 'patients' : 'care team'}</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          placeholder="Search conversations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Thread List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-2xl animate-pulse bg-slate-100" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <h3 className="font-bold text-lg text-slate-900 mb-1">No conversations</h3>
          <p className="text-sm text-slate-500">
            {isDoctor ? 'No active patient conversations.' : 'Start by reporting a health issue to begin a conversation with your care team.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {paged.map((thread) => (
            <button
              key={thread.id}
              onClick={() => navigate(`/chat/${thread.id}`)}
              className="w-full bg-white rounded-2xl p-5 border border-outline-variant/10 hover:shadow-md transition-all flex items-center gap-4 text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm text-slate-900 break-words">{thread.title}</h4>
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                  <span>{thread.patientName}</span>
                  <span>·</span>
                  <span>{new Date(thread.lastActivity).toLocaleDateString()}, {new Date(thread.lastActivity).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
              {perSymptomUnread[thread.id] > 0 && (
                <span className="bg-error text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5 shrink-0">
                  {perSymptomUnread[thread.id]}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                thread.status === 'OPEN' ? 'bg-amber-100 text-amber-700' :
                thread.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                'bg-emerald-100 text-emerald-700'
              }`}>
                {thread.status?.replace('_', ' ')}
              </span>
              <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
            </button>
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
};

export default MessagesPage;
